'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import type { Locale } from '@/app/home/HomeLanding';

function LogoSwap({ scrolled, mobile = false }: { scrolled: boolean; mobile?: boolean }) {
  return (
    <div
      className={`relative shrink-0 ${
        mobile ? 'h-10 w-[46px] md:h-9 md:w-[44px]' : 'h-9 w-[42px] md:w-[44px]'
      }`}
    >
      <img
        src="/assets/images/indofishoil_logo_header1.png"
        alt="Indo Fish Oil"
        className={`pointer-events-none absolute inset-0 h-full w-full object-contain transition-opacity duration-300 ease-out ${
          scrolled ? 'opacity-0' : 'opacity-100'
        }`}
        draggable={false}
      />
      <img
        src="/assets/images/indofishoil_logo_header2.png"
        alt="Indo Fish Oil"
        className={`pointer-events-none absolute inset-0 h-full w-full object-contain transition-opacity duration-300 ease-out ${
          scrolled ? 'opacity-100' : 'opacity-0'
        }`}
        draggable={false}
      />
    </div>
  );
}

export default function Header({ locale = 'en' }: { locale?: Locale }) {
  const [scrollY, setScrollY] = useState(0);
  const [logoScrolled, setLogoScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const lastScrollYRef = useRef(0);
  const mobileMenuScrollRef = useRef<HTMLDivElement>(null);
  const lastMobileMenuScrollYRef = useRef(0);
  const pathname = usePathname();

  const isVietnamese = locale === 'vn';
  const isChinese = locale === 'cn';

  const navItems = isVietnamese
    ? [
        { label: 'Thông số kỹ thuật', shortLabel: 'Thông số', href: '#specifications' },
        { label: 'Logistics & tuân thủ', shortLabel: 'Tuân thủ', href: '#compliance' },
        { label: 'Lợi ích thức ăn', shortLabel: 'Lợi ích', href: '#sustainability' },
        { label: 'Danh mục dịch vụ', shortLabel: 'Dịch vụ', href: '#services' },
        { label: 'Chứng thực thực tế', shortLabel: 'Chứng thực', href: '#case-studies' },
        { label: 'Tài liệu miễn phí', shortLabel: 'Tài liệu', href: '#resources' },
      ]
    : isChinese
      ? [
          { label: '技术参数', shortLabel: '参数', href: '#specifications' },
          { label: '物流与合规', shortLabel: '合规', href: '#compliance' },
          { label: '饲料效益', shortLabel: '效益', href: '#sustainability' },
          { label: '服务目录', shortLabel: '服务', href: '#services' },
          { label: '实证案例', shortLabel: '案例', href: '#case-studies' },
          { label: '免费资料', shortLabel: '资料', href: '#resources' },
        ]
      : [
          { label: 'Technical Specifications', shortLabel: 'Specs', href: '#specifications' },
          { label: 'Logistics & Compliance', shortLabel: 'Compliance', href: '#compliance' },
          { label: 'Industrial Feed Benefits', shortLabel: 'Benefits', href: '#sustainability' },
          { label: 'Service Catalogue', shortLabel: 'Catalogue', href: '#services' },
          { label: 'Field Proof', shortLabel: 'Proof', href: '#case-studies' },
          { label: 'Ungated Resources', shortLabel: 'Resources', href: '#resources' },
        ];

  const languageItems = isVietnamese
    ? [
        { code: 'EN', label: 'Tiếng Anh', href: '/en' },
        { code: 'ID', label: 'Tiếng Indonesia', href: '/id' },
        { code: 'CN', label: 'Tiếng Trung', href: '/cn' },
        { code: 'VN', label: 'Tiếng Việt', href: '/vn' },
      ]
    : isChinese
      ? [
          { code: 'EN', label: '英语', href: '/en' },
          { code: 'ID', label: '印尼语', href: '/id' },
          { code: 'CN', label: '中文', href: '/cn' },
          { code: 'VN', label: '越南语', href: '/vn' },
        ]
      : [
          { code: 'EN', label: 'English', href: '/en' },
          { code: 'ID', label: 'Bahasa Indonesia', href: '/id' },
          { code: 'CN', label: 'Chinese', href: '/cn' },
          { code: 'VN', label: 'Vietnamese', href: '/vn' },
        ];

  const currentLanguage =
    languageItems.find((item) => pathname?.startsWith(item.href)) ?? languageItems[0];
  const homeHref = currentLanguage.href;
  const scrolled = scrollY > 48;

  useEffect(() => {
    const syncScrollPosition = () => {
      const currentScrollY =
        window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;

      setScrollY(currentScrollY);
      if (currentScrollY > lastScrollYRef.current) {
        setLogoScrolled(true);
      } else if (currentScrollY < lastScrollYRef.current) {
        setLogoScrolled(false);
      } else if (lastScrollYRef.current === 0) {
        setLogoScrolled(false);
      }

      lastScrollYRef.current = currentScrollY;
    };

    window.addEventListener('scroll', syncScrollPosition, { passive: true });
    window.addEventListener('resize', syncScrollPosition);
    syncScrollPosition();

    return () => {
      window.removeEventListener('scroll', syncScrollPosition);
      window.removeEventListener('resize', syncScrollPosition);
    };
  }, []);

  useEffect(() => {
    if (!mobileOpen) {
      lastMobileMenuScrollYRef.current = 0;
      return;
    }

    const menuScroller = mobileMenuScrollRef.current;
    if (!menuScroller) return;

    const syncMobileMenuScrollPosition = () => {
      const currentScrollY = menuScroller.scrollTop;

      setScrollY(currentScrollY);
      if (currentScrollY > lastMobileMenuScrollYRef.current) {
        setLogoScrolled(true);
      } else if (currentScrollY < lastMobileMenuScrollYRef.current) {
        setLogoScrolled(false);
      } else if (lastMobileMenuScrollYRef.current === 0) {
        setLogoScrolled(false);
      }

      lastMobileMenuScrollYRef.current = currentScrollY;
    };

    syncMobileMenuScrollPosition();
    menuScroller.addEventListener('scroll', syncMobileMenuScrollPosition, { passive: true });

    return () => {
      menuScroller.removeEventListener('scroll', syncMobileMenuScrollPosition);
    };
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) return;

    const currentScrollY = window.scrollY;
    const bodyStyle = document.body.style;
    const htmlStyle = document.documentElement.style;

    const previousBodyPosition = bodyStyle.position;
    const previousBodyTop = bodyStyle.top;
    const previousBodyWidth = bodyStyle.width;
    const previousHtmlOverflow = htmlStyle.overflow;

    htmlStyle.overflow = 'hidden';
    bodyStyle.position = 'fixed';
    bodyStyle.top = `-${currentScrollY}px`;
    bodyStyle.width = '100%';

    return () => {
      htmlStyle.overflow = previousHtmlOverflow;
      bodyStyle.position = previousBodyPosition;
      bodyStyle.top = previousBodyTop;
      bodyStyle.width = previousBodyWidth;
      window.scrollTo(0, currentScrollY);
    };
  }, [mobileOpen]);

  const ctaLabel = isVietnamese
    ? 'Yêu cầu thông số / COA'
    : isChinese
      ? '申请规格 / COA'
      : 'Request Product Specs / COA';

  const languageLabel = isVietnamese ? 'Ngôn ngữ' : isChinese ? '语言' : 'Language';

  const mobileDescription = isVietnamese
    ? 'Dầu cá cấp thức ăn chăn nuôi năng lượng cao, với hồ sơ EPA/DHA đã được xác minh cho công thức nuôi trồng thủy sản và chăn nuôi.'
    : isChinese
      ? '高能量饲料级鱼油，EPA/DHA 配比已验证，适用于水产与畜牧配方。'
      : 'Premium high-energy feed grade fish oil with verified EPA/DHA profiles for aquaculture and livestock formulations.';

  const downloadLabel = isVietnamese ? 'Tải thông số sản phẩm' : isChinese ? '下载产品规格' : 'Download Product Specs';

  return (
    <header
      className={`fixed top-0 left-0 right-0 transition-all duration-300 ${
        scrolled
          ? 'bg-canvas/95 backdrop-blur-md border-b border-divider shadow-card'
          : 'bg-transparent'
      }`}
      style={{ zIndex: mobileOpen ? 100 : 50 }}
    >
      <div className="max-w-[1280px] mx-auto px-5 md:px-10 h-16 flex items-center justify-between gap-3">
        <a href={homeHref} className="flex items-center gap-2 md:gap-2.5 shrink-0 min-w-0" aria-label="Indo Fish Oil home">
          <LogoSwap scrolled={logoScrolled} mobile />
          <span className="text-[11px] md:text-[12px] font-semibold tracking-[0.14em] uppercase text-charcoal whitespace-nowrap leading-none">
            Indonesia Fish Oil
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((item) => (
            <a
              key={item.label}
              href={item.href}
              title={item.label}
              className="rounded-full px-3.5 py-2 text-[12px] font-medium text-titanium hover:text-charcoal hover:bg-surface transition-colors duration-200 tracking-wide whitespace-nowrap underline decoration-transparent underline-offset-8 hover:decoration-current decoration-1"
            >
              {item.shortLabel}
            </a>
          ))}
        </nav>

        <nav className="hidden md:flex lg:hidden items-center gap-1">
          {navItems.slice(0, 4).map((item) => (
            <a
              key={item.label}
              href={item.href}
              title={item.label}
              className="rounded-full px-2.5 py-2 text-[11px] font-medium text-titanium hover:text-charcoal hover:bg-surface transition-colors duration-200 tracking-wide whitespace-nowrap underline decoration-transparent underline-offset-8 hover:decoration-current decoration-1"
            >
              {item.shortLabel}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <div className="relative hidden md:block">
            <button
              type="button"
              className="inline-flex h-10 items-center gap-2 rounded-full border border-divider bg-surface px-3.5 text-[12px] font-semibold tracking-[0.12em] text-charcoal transition-colors duration-200 hover:bg-white"
              aria-label="Language selector"
              aria-expanded={languageOpen}
              onClick={() => setLanguageOpen(!languageOpen)}
            >
              <span>{currentLanguage.code}</span>
              <svg
                width="12"
                height="12"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
                className={`transition-transform duration-200 ${languageOpen ? 'rotate-180' : ''}`}
              >
                <path
                  d="M4 6l4 4 4-4"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {languageOpen ? (
              <div className="absolute right-0 top-[calc(100%+8px)] z-50 w-44 rounded-2xl border border-divider bg-white p-2 shadow-card">
                {languageItems.map((item) => {
                  const isActive = currentLanguage.code === item.code;
                  return (
                    <a
                      key={item.code}
                      href={item.href}
                      onClick={() => setLanguageOpen(false)}
                      className={`flex items-center justify-between rounded-xl px-3 py-2 text-[12px] transition-colors duration-200 ${
                        isActive ? 'bg-surface text-charcoal' : 'text-titanium hover:bg-surface hover:text-charcoal'
                      }`}
                    >
                      <span className="font-semibold tracking-[0.12em]">{item.code}</span>
                      <span className="text-[11px]">{item.label}</span>
                    </a>
                  );
                })}
              </div>
            ) : null}
          </div>

          <a
            href="#contact"
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 bg-blue text-white text-[13px] font-semibold rounded-full hover:bg-blue-dim transition-colors duration-200"
          >
            {ctaLabel}
          </a>

          <button
            type="button"
            className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-divider bg-surface text-charcoal shrink-0 mr-1"
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              {mobileOpen ? (
                <path
                  d="M6 6l12 12M18 6 6 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              ) : (
                <path
                  d="M4 7h16M4 12h16M4 17h16"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              )}
            </svg>
          </button>
        </div>
      </div>

      {mobileOpen ? (
        <div className="md:hidden fixed inset-0 z-[60] bg-white text-charcoal">
          <div className="flex h-[100dvh] flex-col overflow-hidden bg-white">
            <div className="shrink-0 flex items-center justify-between px-5 pt-6 pb-5 border-b border-divider/70 bg-white">
              <a href={homeHref} className="flex items-center gap-2 min-w-0" aria-label="Indo Fish Oil home">
                <LogoSwap scrolled={logoScrolled} mobile />
                <span className="text-[11px] font-semibold tracking-[0.14em] uppercase text-charcoal whitespace-nowrap leading-none">
                  Indonesia Fish Oil
                </span>
              </a>

              <button
                type="button"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-divider bg-white text-charcoal shadow-sm"
                aria-label="Close menu"
                onClick={() => setMobileOpen(false)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M6 6l12 12M18 6 6 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            <div
              ref={mobileMenuScrollRef}
              className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-6 pb-[calc(24px+env(safe-area-inset-bottom))] bg-white"
            >
              <div className="mb-5 rounded-3xl border border-divider/70 bg-surface p-4 shadow-sm">
                <div className="mb-4 flex items-center justify-between">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-titanium">
                    {languageLabel}
                  </div>
                  <div className="text-[11px] font-medium tracking-[0.08em] text-titanium">
                    {currentLanguage.code}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {languageItems.map((item) => {
                    const isActive = currentLanguage.code === item.code;
                    return (
                      <a
                        key={item.code}
                        href={item.href}
                        onClick={() => setMobileOpen(false)}
                        className={`min-h-14 rounded-2xl border px-3 py-3 text-left transition-all duration-200 active:scale-[0.99] ${
                          isActive
                            ? 'border-blue/20 bg-blue-light text-charcoal shadow-sm'
                            : 'border-divider/70 bg-white text-titanium hover:border-blue/20 hover:bg-surface hover:text-charcoal'
                        }`}
                      >
                        <span className="block text-[12px] font-semibold tracking-[0.14em]">
                          {item.code}
                        </span>
                        <span className="mt-1 block text-[11px] leading-none text-current/70">
                          {item.label}
                        </span>
                      </a>
                    );
                  })}
                </div>
              </div>

              <nav className="space-y-4">
                {navItems.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-between rounded-2xl border border-divider/80 bg-white px-5 py-4 text-[15px] font-medium text-charcoal shadow-[0_8px_24px_rgba(15,23,42,0.06)] transition-transform duration-200 active:scale-[0.99]"
                  >
                    <span>{item.label}</span>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                      className="shrink-0 text-titanium"
                    >
                      <path
                        d="M5 12h14M13 6l6 6-6 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </a>
                ))}
              </nav>

              <div className="mt-6 space-y-4 text-center flex flex-col items-center">
                <a
                  href="#contact"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center rounded-2xl bg-blue px-5 py-4 text-[15px] font-semibold text-white shadow-[0_10px_24px_rgba(30,91,255,0.24)]"
                >
                  {ctaLabel}
                </a>

                <p className="max-w-sm text-[15px] leading-7 text-slate-500">
                  {mobileDescription}
                </p>

                <a
                  href="#resources"
                  onClick={() => setMobileOpen(false)}
                  className="inline-flex items-center justify-center rounded-full bg-blue px-5 py-3 text-[14px] font-semibold text-white shadow-[0_10px_24px_rgba(30,91,255,0.24)]"
                >
                  {downloadLabel}
                </a>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </header>
  );
}
